import set from "@rill/set";
import { defaults, round } from "lodash";
import moment from "moment";
import ActivityDrawer from "../../../parts/activity-drawer/ActivityDrawer";
import JWPlayerVideoModal from "../../../parts/modals/jwplayer-video";
import { formattedTarget } from "../../../utils/actuals";
import {
  buildVideoModalUrl,
  findVideoNeighbors,
} from "../../../utils/video-modal-url";
import ExerciseHistory from "./ExerciseHistory";
import { isAdminOrOwner } from "../../../utils/organization-permissions-helpers";

const ExerciseHistoryController = (app) => {
  app.get(
    "/exercise-history",
    set("heading", "Exercise History"),
    async ({ req, res, load, locals }) => {
      const { query } = req;
      const modal = query?.modal || {};

      defaults(query, {
        from: moment.utc().subtract(6, "months").toDate(),
        to: moment.utc().toDate(),
        primary: "squat",
        type: "primary",
      });

      const { athlete, from, to, primary, type, secondary, complex } = query;
      const userId = athlete || locals.activeUser._id;

      const isAllowToViewAllOrganizationsAthletes = isAdminOrOwner(
        locals.organizationRoleName
      );

      const loadPromises = [
        process.browser &&
          load("exerciseHistory", {
            primary,
            type,
            secondary,
            proxyAthlete: athlete,
            from: moment.utc(from).toDate(),
            to: moment.utc(to).toDate(),
            complex,
          }),
        load("complexes"),
      ];

      if (
        locals.activeUser.organizationId &&
        isAllowToViewAllOrganizationsAthletes &&
        !locals.allAthletesFromOrganization
      ) {
        loadPromises.push(load("allAthletesFromOrganization"));
      }

      if (query.threadId) {
        loadPromises.push(
          load("comments", {
            threadId: query.threadId,
          })
        );
      } else {
        delete locals.comments;
      }

      await Promise.all(loadPromises);

      let prevHref;
      let nextHref;
      if (modal.type === "exercise-set-video") {
        const sequence = [];
        for (const exercise of locals.exerciseHistory?.exercises || []) {
          const exerciseId = String(exercise._id);
          const exPrimary =
            exercise.primary ?? exercise.exercise?.primary ?? "";
          const exSecondary =
            exercise.secondary ?? exercise.exercise?.secondary ?? "";
          const exType = exercise.type ?? exercise.exercise?.type ?? "";
          const exMovement =
            exercise.movement ?? exercise.exercise?.movement ?? "";
          (exercise.sets || []).forEach((set, setIdx) => {
            const videos = set.videos || {};
            Object.keys(videos)
              .map(Number)
              .sort((a, b) => a - b)
              .forEach((vidIdx) => {
                const video = videos[vidIdx];
                if (!video?.videoId && !video?.videoOriginal) return;
                const seqOutcomeObj = set.outcomes?.[vidIdx]?.outcome || {};
                const seqIsPowerlifting =
                  exType === "primary" || exType === "secondary";
                const { units, intensityMeasure } = locals.activeUser;
                const seqPrecision = units === "metric" ? 2 : 1;
                const seqUserMetrics = units === "imperial" ? "lb" : "kg";
                const seqRawLoad = seqOutcomeObj.load;
                const seqLoad = seqRawLoad
                  ? `${round(locals.fromMetric(seqRawLoad, units), seqPrecision)} ${seqUserMetrics}`
                  : 0;
                const seqTarget =
                  seqRawLoad || seqOutcomeObj.reps || seqOutcomeObj.rpe
                    ? formattedTarget(
                        seqOutcomeObj,
                        seqIsPowerlifting,
                        set.percentRepMax || undefined,
                        units,
                        seqLoad,
                        intensityMeasure,
                        intensityMeasure !== "rpe",
                        true
                      ) || undefined
                    : undefined;
                const seqSetNumber = setIdx + 1;
                sequence.push({
                  key: `${exerciseId}:${setIdx}:${vidIdx}`,
                  href: buildVideoModalUrl({
                    modalType: "exercise-set-video",
                    video,
                    exerciseToEdit: exerciseId,
                    setIndex: setIdx,
                    videoIndex: vidIdx,
                    utcDate: exercise.utcDate,
                    primary: exPrimary,
                    secondary: exSecondary,
                    type: exType,
                    movement: exMovement,
                    setNumber: seqSetNumber,
                    setTarget: seqTarget,
                    setComment: set.comments?.[vidIdx] || undefined,
                  }),
                });
              });
          });
        }
        const currentKey = `${query.exerciseToEdit}:${Number(query.setIndex)}:${Number(query.videoIndex)}`;
        ({ prevHref, nextHref } = findVideoNeighbors({ sequence, currentKey }));
      }

      locals.modal = {
        "exercise-set-video": (
          <JWPlayerVideoModal
            mediaId={query.mediaId}
            originalId={query.originalId}
            athleteId={query.uploaderId || userId}
            exerciseToEdit={query.exerciseToEdit}
            reviewedByTheCoach={query.reviewedByTheCoach}
            setIndex={query.setIndex}
            videoIndex={query.videoIndex}
            threadId={query.threadId}
            locals={locals}
            utcDate={query.utcDate}
            primary={query.primary}
            secondary={query.secondary}
            type={query.type}
            movement={query.movement}
            prevHref={prevHref}
            nextHref={nextHref}
            setNumber={query.setNumber}
            setTarget={query.setTarget}
            setComment={query.setComment}
          />
        ),
        "activity-feed": <ActivityDrawer />,
      }[modal.type];

      res.body = <ExerciseHistory />;
    }
  );
};

export default ExerciseHistoryController;
